import { Link } from 'react-router-dom';
import Navigation from '../../../components/feature/Navigation';
import Footer from '../../../components/feature/Footer';
import { useAnimation } from '../../../hooks/useAnimation';

export default function ComplexCommunity() {
  const introRef = useAnimation('fade-in-up');
  const facilitiesRef = useAnimation('stagger-children');
  const layoutRef = useAnimation('fade-in');
  const detailsRef = useAnimation('stagger-children');
  const ctaRef = useAnimation('fade-in-up');

  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      {/* Hero Section */}
      <section className="relative h-[180px] md:h-[400px] flex items-center justify-center overflow-hidden mt-16 md:mt-20">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/f07574ca5fe87aaea161680d7cc76654.jpg"
            alt="커뮤니티"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#1a2b5c]/70 via-[#1a2b5c]/50 to-[#1a2b5c]/70"></div>
        </div>

        <div className="relative z-10 text-center px-4">
          <h1 className="text-[20px] md:text-[48px] font-bold text-white mb-2 md:mb-4" style={{ fontFamily: "'Noto Serif KR', serif" }}>
            커뮤니티
          </h1>
          <p className="text-[11px] md:text-[18px] text-white/90">
            품격 있는 일상을 위한 특화 시설
          </p>
        </div>
      </section>

      {/* Breadcrumb */}
      <section className="bg-gray-50 py-3 md:py-4">
        <div className="max-w-[1200px] mx-auto px-4 md:px-6">
          <div className="flex items-center gap-2 text-[11px] md:text-[14px]">
            <Link to="/" className="text-gray-500 hover:text-[#1a2b5c] transition-colors whitespace-nowrap">홈</Link>
            <i className="ri-arrow-right-s-line text-gray-400"></i>
            <Link to="/complex" className="text-gray-500 hover:text-[#1a2b5c] transition-colors whitespace-nowrap">단지안내</Link>
            <i className="ri-arrow-right-s-line text-gray-400"></i>
            <span className="text-[#1a2b5c] font-medium whitespace-nowrap">커뮤니티</span>
          </div>
        </div>
      </section>

      {/* Intro Section */}
      <section className="py-8 md:py-20 bg-white" ref={introRef}>
        <div className="max-w-[1200px] mx-auto px-4 md:px-6 text-center">
          <span className="inline-block px-3 md:px-6 py-1.5 md:py-2 bg-[#1a2b5c]/10 text-[#1a2b5c] rounded-full text-[10px] md:text-[14px] font-semibold mb-3 md:mb-5">
            프리미엄 커뮤니티
          </span>
          <h2 className="text-[16px] sm:text-[24px] md:text-[40px] font-bold text-[#1a2b5c] mb-3 md:mb-5 leading-tight" style={{ fontFamily: "'Noto Serif KR', serif" }}>
            특별한 소사이어티를 위한<br />
            <span className="text-[#2d4a8f]">고품격 커뮤니티 시설</span>
          </h2>
          <p className="text-[11px] md:text-[17px] text-gray-600 leading-relaxed max-w-[700px] mx-auto">
            입주민의 라이프스타일을 고려한 다양한 커뮤니티 시설로<br />
            품격 있는 일상을 누리실 수 있습니다.
          </p>
        </div>
      </section>

      {/* Community Facilities Grid */}
      <section className="pb-8 md:pb-20 bg-white">
        <div className="max-w-[1200px] mx-auto px-4 md:px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6" ref={facilitiesRef}>
            {[
              { icon: 'ri-book-read-line', title: '북카페 & 작은 도서관', desc: '어린이놀이터와 연계된 활기찬 오픈 라이브러리 & 카페', color: 'from-[#1a2b5c] to-[#2d4a8f]' },
              { icon: 'ri-run-line', title: '피트니스 & 사우나', desc: '입주민을 위한 프라이빗한 실내 운동 공간', color: 'from-emerald-600 to-emerald-700' },
              { icon: 'ri-parent-line', title: '어린이집', desc: '우리집 바로 아래 편리하게 안심하고 아이를 맡길 수 있는 안전한 보육공간', color: 'from-amber-500 to-amber-600' },
              { icon: 'ri-restaurant-line', title: '루프탑 스카이라운지', desc: '청주 시내를 한눈에 바라볼 수 있는 입주민들만을 위한 루프탑 스카이 라운지', color: 'from-sky-600 to-sky-700' },
              { icon: 'ri-movie-line', title: '멀티스튜디오', desc: '단지 내에서 편하게 업무 볼 수 있는 회의실, 오피스룸, 파티룸 제공', color: 'from-purple-600 to-purple-700' },
              { icon: 'ri-shopping-bag-line', title: '프리미엄 조식 서비스', desc: '제철 식자재를 이용한 호텔급 레스토랑 운영의 노하우로 건강한 식사 제공', color: 'from-rose-600 to-rose-700' },
              { icon: 'ri-car-line', title: '프리미엄 컨시어지', desc: '프리미엄 아파트 내 카쉐어링, 방문 세차, 통합 도우미, 런드리 서비스', color: 'from-[#1a2b5c] to-[#0f1a3d]' },
              { icon: 'ri-plant-line', title: '웰컴 라운지', desc: '입주민들의 편안한 쉼터 및 편안한 분위기에서 휴식을 취할 수 있는 웰컴라운지', color: 'from-green-600 to-green-700' },
            ].map((facility, idx) => (
              <div 
                key={idx} 
                className="bg-white border border-gray-100 rounded-xl md:rounded-2xl p-3 md:p-7 shadow-md hover:shadow-xl transition-all group cursor-default animate-item"
                style={{ animationDelay: `${idx * 80}ms` }}
              >
                <div className={`w-9 h-9 md:w-14 md:h-14 bg-gradient-to-br ${facility.color} rounded-lg md:rounded-xl flex items-center justify-center mb-2 md:mb-5 group-hover:scale-110 transition-transform`}>
                  <i className={`${facility.icon} text-white text-base md:text-2xl`}></i>
                </div>
                <h3 className="text-[11px] md:text-[18px] font-bold text-[#1a2b5c] mb-1 md:mb-2.5 leading-tight">{facility.title}</h3>
                <p className="text-[9px] md:text-[14px] text-gray-600 leading-relaxed hidden md:block">{facility.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 2F Community Layout */}
      <section className="py-8 md:py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6">
          <div className="text-center mb-6 md:mb-12">
            <h3 className="text-[16px] md:text-[38px] font-bold text-[#1a2b5c] mb-2 md:mb-4" style={{ fontFamily: "'Noto Serif KR', serif" }}>
              상품소개 커뮤니티 조감(2F)
            </h3>
            <p className="text-[10px] md:text-[16px] text-gray-600">
              2층에 위치한 특별한 소사이어티를 위한 고품격 커뮤니티 시설
            </p>
          </div>
          <div className="relative bg-white rounded-xl md:rounded-3xl overflow-hidden shadow-2xl border border-gray-200" ref={layoutRef}>
            <img
              src="https://static.readdy.ai/image/3b7ebc9e513f68fc027b047fbdd0fbe7/05754e792935e6eb50e2e0638d37307e.png"
              alt="커뮤니티 조감도 2F"
              className="w-full h-auto"
            />
          </div>
          <p className="text-[10px] md:text-[13px] text-gray-400 mt-2 md:mt-4 text-center">
            ※ 상기 커뮤니티 시설은 설계 변경 등에 따라 변경될 수 있으며, 실제와 다를 수 있습니다.
          </p>
        </div>
      </section>

      {/* Facility Details */}
      <section className="py-8 md:py-20 bg-white">
        <div className="max-w-[1200px] mx-auto px-4 md:px-6">
          <h3 className="text-[16px] md:text-[32px] font-bold text-[#1a2b5c] mb-6 md:mb-12 text-center" style={{ fontFamily: "'Noto Serif KR', serif" }}>
            주요 시설 안내
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-8" ref={detailsRef}>
            {[
              { title: '피트니스 센터', desc: '최신 운동 기구를 갖춘 프라이빗 피트니스 센터에서 건강한 라이프스타일을 유지하세요.', image: 'https://readdy.ai/api/search-image?query=Modern%20luxury%20apartment%20fitness%20center%20with%20premium%20exercise%20equipment%2C%20sophisticated%20gym%20interior%20with%20floor-to-ceiling%20windows%20and%20natural%20lighting%2C%20high-end%20residential%20fitness%20facility%20with%20contemporary%20design%2C%20professional%20architectural%20photography%20showing%20upscale%20wellness%20environment&width=800&height=600&seq=community-fitness-001&orientation=landscape', features: ['최신 운동 기구', '프라이빗 공간', '샤워 시설 완비'] },
              { title: '북카페 & 도서관', desc: '조용하고 편안한 분위기에서 독서와 휴식을 즐기실 수 있는 공간입니다.', image: 'https://readdy.ai/api/search-image?query=Elegant%20apartment%20library%20and%20book%20cafe%20with%20comfortable%20seating%2C%20sophisticated%20reading%20lounge%20with%20warm%20lighting%20and%20wooden%20bookshelves%2C%20premium%20residential%20library%20space%20with%20cozy%20atmosphere%2C%20professional%20interior%20photography%20showing%20upscale%20lifestyle%20environment&width=800&height=600&seq=community-library-001&orientation=landscape', features: ['다양한 도서 구비', '카페 공간', '조용한 독서실'] },
              { title: '어린이 놀이터', desc: '아이들이 안전하게 뛰어놀 수 있는 실내외 놀이 공간을 제공합니다.', image: 'https://readdy.ai/api/search-image?query=Modern%20indoor%20children%20playground%20in%20luxury%20apartment%20with%20colorful%20play%20equipment%2C%20safe%20and%20clean%20kids%20play%20area%20with%20soft%20flooring%2C%20premium%20residential%20childcare%20facility%20with%20contemporary%20design%2C%20professional%20photography%20showing%20family-friendly%20environment&width=800&height=600&seq=community-playground-001&orientation=landscape', features: ['안전한 놀이 기구', '실내외 공간', '보호자 휴게 공간'] },
              { title: '스카이 라운지', desc: '청주 시내를 한눈에 조망할 수 있는 루프탑 라운지에서 특별한 시간을 보내세요.', image: 'https://readdy.ai/api/search-image?query=Luxury%20rooftop%20sky%20lounge%20with%20panoramic%20city%20views%20at%20twilight%2C%20sophisticated%20penthouse%20lounge%20with%20elegant%20furniture%20and%20ambient%20lighting%2C%20premium%20residential%20rooftop%20terrace%20with%20contemporary%20design%2C%20professional%20architectural%20photography%20showing%20upscale%20lifestyle%20environment&width=800&height=600&seq=community-skylounge-001&orientation=landscape', features: ['탁 트인 전망', '프리미엄 인테리어', '파티 공간'] },
            ].map((item, idx) => (
              <div 
                key={idx} 
                className="bg-white rounded-xl md:rounded-2xl overflow-hidden shadow-xl border border-gray-100 group cursor-default animate-item"
                style={{ animationDelay: `${idx * 150}ms` }}
              >
                <div className="relative h-[140px] md:h-[280px] overflow-hidden">
                  <img src={item.image} alt={item.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1a2b5c]/80 via-[#1a2b5c]/30 to-transparent"></div>
                  <div className="absolute bottom-3 md:bottom-5 left-3 md:left-6">
                    <span className="text-[14px] md:text-[26px] font-bold text-white" style={{ fontFamily: "'Noto Serif KR', serif" }}>{item.title}</span>
                  </div>
                </div>
                <div className="p-4 md:p-7">
                  <p className="text-[10px] md:text-[15px] text-gray-600 leading-relaxed mb-3 md:mb-5">{item.desc}</p>
                  <ul className="space-y-1.5 md:space-y-2">
                    {item.features.map((f, i) => (
                      <li key={i} className="flex items-center gap-2 md:gap-3">
                        <i className="ri-checkbox-circle-fill text-[#1a2b5c] text-sm md:text-lg"></i>
                        <span className="text-[10px] md:text-[14px] text-gray-700">{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-10 md:py-20 bg-gradient-to-br from-[#1a2b5c] to-[#0f1a3d] relative overflow-hidden" ref={ctaRef}>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-white rounded-full blur-3xl"></div>
        </div>
        <div className="max-w-[1200px] mx-auto px-4 md:px-6 text-center relative z-10">
          <h2 className="text-[16px] md:text-[38px] font-bold text-white mb-3 md:mb-6" style={{ fontFamily: "'Noto Serif KR', serif" }}>
            커뮤니티 시설을 직접 확인해보세요
          </h2>
          <p className="text-[11px] md:text-[17px] text-white/90 mb-6 md:mb-10">
            모델하우스 방문을 통해 프리미엄 커뮤니티 시설을<br />직접 경험하실 수 있습니다.
          </p>
          <div className="flex items-center justify-center gap-2 md:gap-3 flex-wrap">
            <Link to="/reservation"
              onClick={() => { if (typeof (window as any).gtag_report_conversion === 'function') (window as any).gtag_report_conversion(); }}
              className="inline-flex items-center gap-1.5 md:gap-2 px-5 md:px-10 py-2.5 md:py-4 bg-white text-[#1a2b5c] rounded-full text-[11px] md:text-[16px] font-semibold hover:bg-gray-100 transition-all shadow-xl whitespace-nowrap">
              방문예약 하기 <i className="ri-arrow-right-line"></i>
            </Link>
            <a href="tel:1555-5475"
              onClick={() => { if (typeof (window as any).gtag_report_conversion === 'function') (window as any).gtag_report_conversion(); }}
              className="inline-flex items-center gap-1.5 md:gap-2 px-5 md:px-10 py-2.5 md:py-4 bg-transparent text-white rounded-full text-[11px] md:text-[16px] font-semibold hover:bg-white/10 transition-all border-2 border-white whitespace-nowrap">
              <i className="ri-phone-line"></i> 전화 상담하기
            </a>
          </div>
        </div>
      </section>

      {/* 모바일 고정 플로팅 CTA 바 */}
      <div className="fixed bottom-0 left-0 right-0 z-[90] md:hidden">
        <div className="h-[2px] bg-gradient-to-r from-[#b8972a] via-[#f0d060] to-[#b8972a] cta-glow"></div>
        <div className="bg-gradient-to-r from-[#0a1229] via-[#1a2b5c] to-[#0a1229] px-3 py-3 flex items-center gap-2.5 shadow-2xl">
          <Link
            to="/reservation"
            onClick={() => { if (typeof (window as any).gtag_report_conversion === 'function') (window as any).gtag_report_conversion(); }}
            className="flex-1 flex items-center justify-center gap-2 py-3.5 rounded-xl whitespace-nowrap cursor-pointer relative overflow-hidden group cta-pulse"
            style={{ background: 'linear-gradient(135deg, #b8972a 0%, #f0d060 40%, #c9a84c 70%, #b8972a 100%)' }}
          >
            <div className="absolute inset-0 bg-white/0 group-active:bg-white/10 transition-all"></div>
            <div className="absolute inset-0 pointer-events-none">
              <div className="absolute top-0 bottom-0 w-1/3 bg-gradient-to-r from-transparent via-white/40 to-transparent cta-shimmer"></div>
            </div>
            <i className="ri-calendar-check-line text-[#0a1229] text-[18px] relative z-10"></i>
            <span className="text-[14px] font-bold text-[#0a1229] relative z-10">방문예약</span>
          </Link>
          <a
            href="tel:1555-5475"
            onClick={() => { if (typeof (window as any).gtag_report_conversion === 'function') (window as any).gtag_report_conversion(); }}
            className="flex-1 flex items-center justify-center gap-2 py-3.5 bg-white rounded-xl whitespace-nowrap cursor-pointer relative overflow-hidden group border-2 border-white/30"
          >
            <div className="absolute inset-0 bg-[#1a2b5c]/0 group-active:bg-[#1a2b5c]/10 transition-all"></div>
            <div className="absolute inset-0 pointer-events-none">
              <div className="absolute top-0 bottom-0 w-1/3 bg-gradient-to-r from-transparent via-[#1a2b5c]/15 to-transparent cta-shimmer" style={{ animationDelay: '1.1s' }}></div>
            </div>
            <i className="ri-phone-fill text-[#1a2b5c] text-[18px] relative z-10"></i>
            <span className="text-[14px] font-bold text-[#1a2b5c] relative z-10">전화상담</span>
          </a>
        </div>
      </div>

      <Footer />
    </div>
  );
}
